import {
  computed,
  ref,
  useRouter,
  useContext,
  onMounted
} from "@nuxtjs/composition-api";
import debounce from "lodash-es/debounce";
import cartGetters from "../../getters/cartGetters";
import {
  useUiNotification,
  useExternalCheckout,
  useImage
} from "@wemade-vsf/composables";
import { useProduct } from "@wemade-vsf/catalog/lib/runtime/product/composables/useProduct";
import { useCart, useUser } from "@wemade-vsf/customer";
import { ProductStockStatus } from "@wemade-vsf/magento-api";
import { useEvent } from "@wemade-vsf/composables";
function useCartView() {
  const { localePath, localeRoute, app: { i18n, $vsf, $wm } } = useContext();
  const { initializeCheckout } = useExternalCheckout();
  const { getMagentoImage, imageSizes } = useImage();
  const router = useRouter();
  const { getProductPath } = useProduct();
  const {
    cart,
    removeItem,
    updateItemQty,
    load: loadCart,
    loading
  } = useCart();
  const { isAuthenticated } = useUser();
  const { send: sendNotification, notifications } = useUiNotification();
  const products = computed(() => cartGetters.getItems(cart.value).filter(Boolean).map((item) => {
    var _a;
    return {
      ...item,
      product: {
        ...item.product,
        ...[(_a = item.configured_variant) != null ? _a : {}],
        original_sku: item.product.sku
      }
    };
  }));
  const totals = computed(() => cartGetters.getTotals(cart.value));
  const discount = computed(() => -cartGetters.getDiscountAmount(cart.value));
  const totalItems = computed(() => cartGetters.getTotalItems(cart.value));
  const getAttributes = (product) => product.configurable_options || [];
  const getBundles = (product) => {
    var _a;
    return ((_a = product.bundle_options) == null ? void 0 : _a.map((b) => b.values).flat()) || [];
  };
  const isRemoveModalVisible = ref(false);
  const itemToRemove = ref();
  onMounted(async () => {
    if (!cart.value.id) {
      await loadCart();
    }
    useEvent("cart:view", cart.value);
  });
  const goToCheckout = async () => {
    var _a, _b;
    if ($vsf.$magento.config.externalCheckout.enable) {
      const redirectUrl = initializeCheckout({ baseUrl: "/checkout/user-account" });
      await router.push(localePath(redirectUrl));
    } else if ((_a = $wm == null ? void 0 : $wm.checkout) == null ? void 0 : _a.useCartPage) {
      await router.push(localeRoute({ name: "checkout-cart" }));
    } else {
      const checkoutUrl = ((_b = $wm == null ? void 0 : $wm.checkout) == null ? void 0 : _b.checkoutRoute) ? localeRoute({ name: $wm.checkout.checkoutRoute }) : localeRoute({ name: "checkout-account" });
      await router.push(checkoutUrl);
    }
  };
  const showRemoveItemModal = ({ product }) => {
    if (notifications.value.length > 0) {
      notifications.value[0].dismiss();
    }
    isRemoveModalVisible.value = true;
    itemToRemove.value = product;
  };
  const removeItemAndSendNotification = async (product) => {
    await removeItem({ product });
    isRemoveModalVisible.value = false;
    sendNotification({
      id: Symbol("product_removed"),
      message: i18n.t("{0} has been successfully removed from your cart", {
        0: cartGetters.getItemName(
          product
        )
      }),
      type: "success",
      icon: "check",
      persist: false,
      title: "Product removed"
    });
  };
  const delayedUpdateItemQty = debounce(
    (params) => updateItemQty(params),
    1e3
  );
  const isInStock = (product) => cartGetters.getStockStatus(product) === ProductStockStatus.InStock;
  return {
    showRemoveItemModal,
    removeItemAndSendNotification,
    delayedUpdateItemQty,
    goToCheckout,
    getAttributes,
    getBundles,
    isInStock,
    getMagentoImage,
    getProductPath,
    loading,
    isAuthenticated,
    products,
    isRemoveModalVisible,
    itemToRemove,
    totals,
    totalItems,
    imageSizes,
    discount,
    cartGetters
  };
}
var useCartView_default = useCartView;
export * from "./useCartView";
export {
  useCartView_default as default,
  useCartView
};
